import FilterBottomSection from '@components/FilterComponents/FilterBottonSection';
import FilterTopSection from '@components/FilterComponents/FilterTopSection';
import Modal from '@components/Modal';
import Portal from '@components/Modal/ModalPortal';
import { setStringArr } from '@util/filters';
import { objectToQueryParams } from '@util/queryParams/objectToQueryParams';
import { queryParamsToObject } from '@util/queryParams/queryParamsToObject';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AllFiltersSection } from './AllFiltersSection';
import FilterButton from './FilterButton';
import { ButtonContainer } from './styles';

interface FilterObject {
  title: string;
  value: string;
}

export interface SelectedFilters {
  specializations?: string[];
  languages?: string[];
  roles?: string[];
  currentPage?: string;
  shopIds?: string;
}

export type SetArrType = 'specializations' | 'languages' | 'roles';

interface AdvisorSearchFilterProps {
  specializations: FilterObject[];
  languages: FilterObject[];
  roles: FilterObject[];
  onApply: (selectedFilters: any) => void;
  onClear: () => void;
}

const AdvisorSearchFilter: FC<AdvisorSearchFilterProps> = ({
  specializations,
  languages,
  roles,
  onApply,
}) => {
  const t = useTranslations();
  const dropdownRef = useRef<any>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  //set up the modal
  let modal = useRef<any>(null);

  const openModalSection = () => {
    modal.current?.setOpen(true);
  };

  const closeAllModals = () => {
    modal.current?.toggle(false);
  };

  useEffect(() => {
    setFiltersFromQueryParamsOnFirstLoad();
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setSelectedFilter('');
    }
  };

  const setFiltersFromQueryParamsOnFirstLoad = () => {
    const queryString = searchParams.toString();
    if (queryString) {
      const selectedFilters: SelectedFilters = queryParamsToObject(queryString);
      if (selectedFilters.specializations?.length) {
        setSelectedSpecializations(selectedFilters.specializations);
      }
      if (selectedFilters.languages?.length) {
        setSelectedLanguages(selectedFilters.languages);
      }
      if (selectedFilters.roles?.length) {
        setSelectedRoles(selectedFilters.roles);
      }
    }
  };

  const handleSetSelectedFilter = (f: string) => {
    if (f === 'allFilters') {
      openModalSection();
      setSelectedFilter('');
    } else {
      if (f === selectedFilter) setSelectedFilter('');
      else setSelectedFilter(f);
    }
  };

  const handleSetArr = (type: string, b: string | string[]): void => {
    switch (type) {
      case 'specializations': {
        const specialization = setStringArr(selectedSpecializations, b);
        setSelectedSpecializations(specialization ?? []);
        break;
      }
      case 'languages':
        if (Array.isArray(b)) {
          setSelectedLanguages(b);
        } else {
          setSelectedLanguages(b.length > 0 ? b.split(',') : []);
        }
        break;
      case 'roles': {
        const role = setStringArr(selectedRoles, b);
        setSelectedRoles(role ?? []);
        break;
      }
      default:
        console.error('AdvisorSearchFilter: handleSetArr: invalid type');
    }
  };

  const onClickApply = () => {
    let selectedFilters: SelectedFilters = {
      ...queryParamsToObject(searchParams.toString()),
    };

    if (selectedSpecializations.length) {
      selectedFilters.specializations = selectedSpecializations;
    } else {
      selectedFilters.specializations = [];
    }

    if (selectedLanguages.length) {
      selectedFilters.languages = selectedLanguages;
    } else {
      selectedFilters.languages = [];
    }

    if (selectedRoles.length) {
      selectedFilters.roles = selectedRoles;
    } else {
      selectedFilters.roles = [];
    }

    const queryParams = objectToQueryParams(selectedFilters);
    router.push(pathname + '?' + queryParams);
    onApply({ selectedFilters });
    closeAllModals();
  };

  const onClickClear = (type: string) => {
    let selectedFilters: SelectedFilters = {};

    if (selectedSpecializations.length) {
      selectedFilters.specializations = [];
    }
    if (selectedLanguages.length) {
      selectedFilters.languages = [];
    }
    if (selectedRoles.length) {
      selectedFilters.roles = [];
    }

    selectedFilters = {
      ...queryParamsToObject(searchParams.toString()),
      ...selectedFilters,
    };
    const queryParams = objectToQueryParams(selectedFilters);
    router.push(pathname + '?' + queryParams);

    setSelectedSpecializations([]);
    setSelectedLanguages([]);
    setSelectedRoles([]);

    onApply({ selectedFilters });
  };

  const filters = [
    {
      type: 'specializations',
      title: t('Specialization'),
      array: specializations,
      selectedArray: selectedSpecializations,
    },
    {
      type: 'languages',
      title: t('Languages Spoken'),
      array: languages,
      selectedArray: selectedLanguages,
    },
    {
      type: 'roles',
      title: t('Role'),
      array: roles,
      selectedArray: selectedRoles,
    },
  ];

  const filterButtonProps = {
    handleSetSelectedFilter,
    filters,
    handleSetArr,
    onClickApply,
    onClickClear,
  };

  const allFiltersProps = {
    filters,
    handleSetArr,
    handleSetSelectedFilter: handleSetSelectedFilter,
  };

  return (
    <FilterContainer ref={dropdownRef}>
      <ButtonContainer>
        <FilterButton
          {...filterButtonProps}
          title={t('Filters')}
          name={'allFilters'}
          selected={'allFilters' === selectedFilter}
        />
      </ButtonContainer>

      <Portal>
        {/* // Portal will render at the bottom of the page, look at page.tsx for implementation */}
        <Modal
          top={<FilterTopSection heading={t('All Filters')} onClose={closeAllModals} />}
          center={<AllFiltersSection {...allFiltersProps} />}
          bottom={
            <FilterBottomSection
              isApplyDisable={false}
              onClickApply={onClickApply}
              onClear={() => onClickClear('allFilters')}
            />
          }
          ref={modal}
        />
      </Portal>
    </FilterContainer>
  );
};

export default AdvisorSearchFilter;

const FilterContainer = styled.div``;
